import { useNavigate, useParams } from "react-router-dom";
import { protocolsApi } from "../../services/protocols/protocolsApi";
import { Button } from "primereact/button";
import { FormikProvider, useFormik } from "formik";
import { Skeleton } from "primereact/skeleton";
import Error from "../../components/common/Error";
import { Card } from "primereact/card";
import { ProtocolResponse, ProtocolStudentFilter, ProtocolStudentResponse } from "../../models/dtos";
import { teachersApi } from "../../services/teachers/teachersApi";
import { subjectsApi } from "../../services/nomenclatures/subjectsApi";
import { specialitiesApi } from "../../services/nomenclatures/specialitiesApi";
import { sessionTypesApi } from "../../services/nomenclatures/sessionTypesApi";
import { academicYearsApi } from "../../services/nomenclatures/academicYearsApi";
import { semesterTypesApi } from "../../services/nomenclatures/semesterTypesApi";
import { ProgressSpinner } from "primereact/progressspinner";
import { useEffect, useRef, useState } from "react";
import { Toast } from "primereact/toast";
import ProtocolGradesList from "./ProtocolGradesList";
import { examTypesApi } from "../../services/nomenclatures/examTypesApi";
import ActionButtons from "./ActionButtons";
import ProtocolStudentsList from "./ProtocolStudentsList";
import { TabPanel } from "primereact/tabview";
import { TabView } from "primereact/tabview";
import ProtocolCommission from "./ProtocolCommission";
import ProtocolVersionsList from "./ProtocolVersionsList";
import { Tag } from "primereact/tag";
import ProtocolStudentsFilter from "./ProtocolStudentsFilter";
import { Dialog } from "primereact/dialog";
import ProtocolMainFields from "./ProtocolMainFields";

const ProtocolDetails = () => {
  const { id } = useParams();
  const protocolId = id ? parseInt(id, 10) : 0;
  const toast = useRef<Toast>(null);
  const navigate = useNavigate();

  const {
    data: protocol,
    error: protocolError,
    isLoading: protocolLoading,
  } = protocolsApi.useGetProtocolQuery(protocolId, {
    skip: !id,
  });

  const [filter, setFilter] = useState<ProtocolStudentFilter>({
    showHigherSemesterStudents: false,
    showForeignStudents: true,
    showStudyingInEnglish: false,
    studentFnOrName: undefined,
  });

  const {
    data: protocolStudents,
    error: protocolStudentsError,
    isLoading: protocolStudentsLoading,
    isFetching: protocolStudentsFetching,
  } = protocolsApi.useGenerateProtocolStudentListQuery(
    {
      protocolId,
      filter: filter,
    },
    {
      skip: !id,
    },
  );

  const {
    data: protocolVersions,
    error: protocolVersionsError,
    isLoading: protocolVersionsLoading,
  } = protocolsApi.useGetProtocolVersionsQuery(protocolId, {
    skip: !id,
  });

  const [updateProtocol, { isLoading: isUpdateLoading, error: updateError, isSuccess: isUpdateSuccess }] =
    protocolsApi.useUpdateProtocolMutation();

  const [checkDuplicateProtocol, { isLoading: isCheckingDuplicate }] = protocolsApi.useCheckDuplicateProtocolMutation();

  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  const [duplicateProtocol, setDuplicateProtocol] = useState<ProtocolResponse | null>(null);

  const { data: teachers } = teachersApi.useGetTeachersQuery({});
  const { data: subjects } = subjectsApi.useGetSubjectsQuery();
  const { data: specialities } = specialitiesApi.useGetSpecialitiesAsFlatListQuery();
  const { data: sessionTypes } = sessionTypesApi.useGetSessionTypesQuery();
  const { data: examTypes } = examTypesApi.useGetExamTypesQuery();
  const { data: academicYears } = academicYearsApi.useGetAcademicYearsQuery();
  const { data: semesterTypes } = semesterTypesApi.useGetSemesterTypesQuery();

  const [triggerGetSubjectsBySpecialityIdQuery] = subjectsApi.useLazyGetSubjectsBySpecialityIdQuery();
  const [triggerGetSubjectsByFacultyOfSpecialityQuery] = subjectsApi.useLazyGetSubjectsByFacultyOfSpecialityQuery();

  const [setProtocolStudents, { isLoading: isSetProtocolStudentsLoading, error: setProtocolStudentsError }] =
    protocolsApi.useSetProtocolStudentsMutation();

  const [selectedStudents, setSelectedStudents] = useState<ProtocolStudentResponse[]>([]);

  const [useSubjectsByFaculty, setUseSubjectsByFaculty] = useState<boolean>(false);

  const handleSelectionChange = (students: ProtocolStudentResponse[]) => {
    setSelectedStudents(students);
  };

  const handleAddToProtocol = () => {
    if (!id) return;
    setProtocolStudents({ protocolId, protocolStudents: selectedStudents });
  };

  const emptyInitialValues: Partial<ProtocolResponse> = {
    protocolId: 0,
    protocolNumber: "",
    teacherId: undefined,
    substituteTeacherId: undefined,
    specialityId: undefined,
    subjectId: undefined,
    issueDate: undefined,
    examDate: undefined,
    secondExamDate: undefined,
    sessionTypeId: undefined,
    examTypeId: undefined,
    academicYearId: undefined,
    semesterTypeId: undefined,
    semester: undefined,
  };

  const formik = useFormik<Partial<ProtocolResponse>>({
    initialValues: !id
      ? emptyInitialValues
      : {
          ...protocol?.data,
          issueDate: protocol?.data.issueDate ? new Date(protocol.data.issueDate) : undefined,
          examDate: protocol?.data.examDate ? new Date(protocol.data.examDate) : undefined,
          secondExamDate: protocol?.data.secondExamDate ? new Date(protocol.data.secondExamDate) : undefined,
        },
    enableReinitialize: true,
    onSubmit: async (values) => {
      const isNewProtocol = !id || id === "0" || (protocol?.data?.protocolId ?? 0) === 0;

      if (isNewProtocol) {
        try {
          const duplicateCheck = await checkDuplicateProtocol({
            subjectId: values.subjectId!,
            specialityId: values.specialityId!,
            semester: values.semester ?? 0,
            academicYearId: values.academicYearId!,
            sessionTypeId: values.sessionTypeId!,
            protocolId: 0,
          }).unwrap();

          if (duplicateCheck?.data) {
            setDuplicateProtocol(duplicateCheck.data);
            setShowDuplicateDialog(true);
            return;
          }
        } catch (error) {
          console.error("Error checking for duplicate protocol:", error);
        }
      }

      updateProtocol(values)
        .unwrap()
        .then((result) => {
          if (result && "data" in result) {
            navigate(`/protocols/${id ? id : result.data}`);
          }
        });
    },
  });

  const availableSubstituteTeachers = teachers?.data.filter((teacher) => teacher.teacherId !== formik.values.teacherId);

  useEffect(() => {
    if (isUpdateSuccess) {
      toast.current?.show({ severity: "success", summary: "Обновяване", detail: "Протоколът е обновен успешно." });
    }
  }, [isUpdateSuccess]);

  useEffect(() => {
    if (protocol?.data?.examTypeId === 18) {
      navigate(`/protocols/${id}/recognition`, { replace: true });
    }
  }, [protocol, id, navigate]);

  useEffect(() => {
    const isNewProtocol = !id || id === "0" || (protocol?.data?.protocolId ?? 0) === 0;
    if (!isNewProtocol) {
      setShowDuplicateDialog(false);
      setDuplicateProtocol(null);
    }
  }, [id, protocol]);

  // For create mode (no id), we don't load protocol data and want a blank form
  if (id && protocolLoading) return <Skeleton className="w-full h-2rem" />;
  if (id && protocolError) return <Error error={protocolError} />;

  return (
    <FormikProvider value={formik}>
      <div>
        <Toast ref={toast} />

        {isUpdateLoading && <ProgressSpinner />}
        {updateError && <Error error={updateError} />}

        <form onSubmit={formik.handleSubmit}>
          <Card
            title={`Протокол ${protocol?.data.protocolNumber || ""}`}
            footer={<Button type="submit" label="Save" icon="pi pi-save" />}
            className="mb-4 shadow-4"
            pt={{
              footer: {
                className: "flex justify-content-center",
              },
            }}
          >
            {id && protocol?.data && (
              <div className="grid">
                <div className="col-4">
                  <div className="grid">
                    <div className="field col-4 flex flex-column">
                      <label htmlFor="currentVersion">Текуща версия</label>
                      <span id="currentVersion">{protocol.data.currentVersion || "-"}</span>
                    </div>
                    <div className="field col-4 flex flex-column">
                      <label htmlFor="currentStatus">Текущ статус</label>
                      <Tag
                        id="currentStatus"
                        value={protocol.data.currentStatus || "-"}
                        severity={protocol.data.currentStatus === "SIGNED" ? "success" : "info"}
                      />
                    </div>
                    <div className="field col-4 flex flex-column">
                      <label htmlFor="isSigned">Подписан</label>
                      <span id="isSigned">{protocol.data.isSigned ? "Да" : "Не"}</span>
                    </div>
                    <div className="field col-4 flex flex-column">
                      <label htmlFor="signedBy">Подписан от</label>
                      <span id="signedBy">{protocol.data.signedBy || "-"}</span>
                    </div>
                    <div className="field col-4 flex flex-column">
                      <label htmlFor="signedOn">Дата на подписване</label>
                      <span id="signedOn">
                        {protocol.data.signedOn ? new Date(protocol.data.signedOn).toLocaleDateString("bg-BG") : "-"}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="col-8">
                  <Card>
                    <ActionButtons protocolId={protocolId} />
                  </Card>
                </div>
              </div>
            )}

            <ProtocolMainFields
              teachers={teachers?.data || []}
              substituteTeachers={availableSubstituteTeachers || []}
              specialities={specialities?.data || []}
              sessionTypes={sessionTypes?.data || []}
              examTypes={examTypes?.data || []}
              academicYears={academicYears?.data || []}
              semesterTypes={semesterTypes?.data || []}
              useSubjectsByFaculty={useSubjectsByFaculty}
              setUseSubjectsByFaculty={setUseSubjectsByFaculty}
              loadSubjects={(specialityId) =>
                useSubjectsByFaculty
                  ? triggerGetSubjectsByFacultyOfSpecialityQuery(specialityId)
                  : triggerGetSubjectsBySpecialityIdQuery(specialityId)
              }
              subjectLabel={protocol?.data?.subjectName}
            />

            <div className="mt-4">
              <label>Комисия / преподаватели</label>
              <ProtocolCommission />
            </div>
          </Card>
        </form>

        {id && (
          <TabView>
            <TabPanel header="Оценки" leftIcon="pi pi-chart-bar mr-2">
              {protocol?.data && <ProtocolGradesList protocol={protocol.data} />}
            </TabPanel>
            <TabPanel header="Списък с потенциални студенти" leftIcon="pi pi-users mr-2">
              <ProtocolStudentsFilter
                initialFilter={filter}
                onFilterChange={(newFilter) => {
                  setFilter(newFilter);
                }}
              />
              {(protocolStudentsLoading || protocolStudentsFetching) && <Skeleton className="w-full h-2rem" />}
              {protocolStudentsError && <Error error={protocolStudentsError} />}
              {protocolStudents?.data && (
                <>
                  <ProtocolStudentsList
                    protocolId={parseInt(id!)}
                    protocolStudents={protocolStudents.data}
                    selectedStudents={selectedStudents}
                    onSelectionChange={handleSelectionChange}
                  />
                  <Button label="Добави в протокол" icon="pi pi-plus" onClick={handleAddToProtocol} />
                  {isSetProtocolStudentsLoading && <ProgressSpinner />}
                  {setProtocolStudentsError && <Error error={setProtocolStudentsError} />}
                </>
              )}
            </TabPanel>
            <TabPanel header="Версии" leftIcon="pi pi-list mr-2">
              {protocolVersionsLoading && <Skeleton className="w-full h-2rem" />}
              {protocolVersionsError && <Error error={protocolVersionsError} />}
              {protocolVersions?.data && (
                <ProtocolVersionsList protocolId={parseInt(id!)} protocolVersions={protocolVersions.data} />
              )}
            </TabPanel>
          </TabView>
        )}

        <Dialog
          header="Дублиран протокол"
          visible={showDuplicateDialog}
          onHide={() => setShowDuplicateDialog(false)}
          style={{ width: "50vw" }}
        >
          <div>
            <p>Протокол с тези параметри вече съществува:</p>
            {duplicateProtocol && (
              <div className="mt-3">
                <div className="grid">
                  <div className="col-12">
                    <strong>Номер на протокол:</strong> {duplicateProtocol.protocolNumber}
                  </div>
                  <div className="col-12">
                    <strong>Дисциплина:</strong> {duplicateProtocol.subjectName}
                  </div>
                  <div className="col-12">
                    <strong>Специалност:</strong> {duplicateProtocol.specialityName}
                  </div>
                  <div className="col-12">
                    <strong>Семестър:</strong> {duplicateProtocol.semester}
                  </div>
                  <div className="col-12">
                    <strong>Академична година:</strong> {duplicateProtocol.academicYearName}
                  </div>
                  <div className="col-12">
                    <strong>Сесия:</strong> {duplicateProtocol.sessionTypeName}
                  </div>
                </div>
              </div>
            )}
            <div className="flex justify-content-end gap-2 mt-3">
              <Button
                label="Отиди към съществуващия протокол"
                onClick={() => {
                  if (duplicateProtocol) {
                    setShowDuplicateDialog(false);
                    setDuplicateProtocol(null);
                    navigate(`/protocols/${duplicateProtocol.protocolId}`);
                  }
                }}
              />
              <Button
                label="Създай нов протокол"
                severity="secondary"
                onClick={async () => {
                  setShowDuplicateDialog(false);
                  setDuplicateProtocol(null);
                  const values = formik.values;
                  updateProtocol(values)
                    .unwrap()
                    .then((result) => {
                      if (result && "data" in result) {
                        navigate(`/protocols/${id ? id : result.data}`);
                      }
                    });
                }}
              />
            </div>
          </div>
        </Dialog>
      </div>
    </FormikProvider>
  );
};

export default ProtocolDetails;
